import React, { forwardRef, useState, useEffect, useRef, useMemo } from "react";
import {
  Autocomplete as AutocompleteBase,
  TextField as TextFieldBase,
  Checkbox,
  MenuItem,
  ListItemText,
  Divider,
  Box,
  Button,
  Chip,
  Paper,
  Alert,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RpVirtualList from "@/components/virtual/RpVirtualList";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";

type RpAutocompleteOption = {
  value: string;
  label: string;
};

type RpAutocompleteWithApplyProps = Record<string, any> & {
  value?: string[];
  onChange?: (_value: string[]) => void;
  options?: RpAutocompleteOption[];
};

const Autocomplete = AutocompleteBase as React.ElementType;
const TextField = TextFieldBase as React.ElementType;

const UNCHECKED = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKED = <CheckBoxIcon fontSize="small" />;

const SELECT_ALL = "__SELECT_ALL__";
const CLEAR_ALL = "__CLEAR_ALL__";
const NO_OPTIONS = "__NO_OPTIONS__";
const isPseudo = (v: string) =>
  v === SELECT_ALL || v === CLEAR_ALL || v === NO_OPTIONS;
const uniqueValues = (values: string[]) => [...new Set(values.filter(Boolean))];
const hasSameValues = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  const rightValues = new Set(right);
  return left.every((value) => rightValues.has(value));
};

const VirtualListbox = forwardRef(function VirtualListbox(
  { children, className, role, sx, onEndReached, ...listboxProps }: any,
  listboxRef: any
) {
  const items = React.Children.toArray(children);
  const height = Math.min(Math.max(items.length * 42, 42), 240);

  return (
    <Box
      component="ul"
      ref={listboxRef}
      className={className}
      role={role}
      {...listboxProps}
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        "& .custom-list-item": { listStyle: "none" },
        ...sx,
      }}
    >
      <RpVirtualList
        items={items}
        useWindowScroll={false}
        style={{ height, overflowX: "hidden" }}
        endReached={onEndReached}
        renderItem={(item: React.ReactNode) => item}
      />
    </Box>
  );
});

export default function RpAutocompleteWithApply({
  value,
  onChange,
  options = [],
  applyLabel = "Apply Filters",
  clearLabel = "Clear All",
  placeholder = "",
  error = false,
  helperText = "",
  staticLabel = false,
  disabled = false,
  size = "small",
  variant = "outlined",
  name,
  onBlur,
  noOptionsText = "No data available",
  useVirtualList = false,
  onListEndReached,
  ...rest
}: RpAutocompleteWithApplyProps) {
  const committed = useMemo(
    () => (Array.isArray(value) ? value : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(value)]
  );

  const [pending, setPending] = useState(committed);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const filteredOptionsRef = useRef(options);
  const isApplyDisabled =
    pending.length === 0 && committed.length === 0
      ? true
      : hasSameValues(pending, committed);

  useEffect(() => {
    setPending(committed);
  }, [committed]);

  const ref = useRef(null);
  ref.current = { pending, onChange, applyLabel, isApplyDisabled };

  const PaperWithApply = useMemo(
    () =>
      function CustomPaper({ children, ...paperProps }: any) {
        const ctx = ref.current ?? ({} as any);
        return (
          <Paper
            {...paperProps}
            sx={{
              borderRadius: "var(--radius-container)",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "var(--shadow-floating)",
              overflow: "hidden",
              ...paperProps.sx,
            }}
          >
            {children}
            <Box
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                px: 1.75,
                py: 1.75,
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="primary"
                fullWidth
                disabled={ctx.isApplyDisabled}
                sx={{
                  minHeight: 46,
                  borderRadius: "var(--radius-control)",
                  fontSize: "0.75rem",
                  textTransform: "none",
                }}
                onClick={() => {
                  if (ctx.isApplyDisabled) return;
                  ctx.onChange?.(ctx.pending);
                  setOpen(false);
                }}
              >
                {ctx.applyLabel}
              </Button>
            </Box>
          </Paper>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const augmentedOptions = useMemo(
    () => [
      { value: SELECT_ALL, label: "" },
      { value: CLEAR_ALL, label: "" },
      ...options,
    ],
    [options]
  );

  const autocompleteSx = {
    mt: "8px",
    ...rest.sx,
  };

  // Committed chips for display
  const committedChips = options.filter((o) => committed.includes(o.value));

  return (
    <Autocomplete
      options={augmentedOptions}
      multiple
      // value always reflects pending — MUI add/remove logic works correctly
      value={options.filter((o) => pending.includes(o.value))}
      getOptionLabel={(o: RpAutocompleteOption) => o?.label ?? ""}
      getOptionKey={(o: RpAutocompleteOption) => o?.value ?? o?.label ?? ""}
      isOptionEqualToValue={(
        o: RpAutocompleteOption,
        v: RpAutocompleteOption
      ) => o.value === v?.value}
      getOptionDisabled={(o: RpAutocompleteOption) => isPseudo(o.value)}
      filterOptions={(opts: RpAutocompleteOption[], state: any) => {
        const pseudo = opts.filter((o) => isPseudo(o.value));
        const filtered = opts
          .filter((o) => !isPseudo(o.value))
          .filter((o) =>
            o.label.toLowerCase().includes(state.inputValue.toLowerCase())
          );
        filteredOptionsRef.current = filtered;
        if (filtered.length === 0) {
          return [...pseudo, { value: NO_OPTIONS, label: "" }];
        }
        return [...pseudo, ...filtered];
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(_e: React.SyntheticEvent, reason: string) => {
        if (reason === "blur" || reason === "escape") {
          setPending(committed);
          setOpen(false);
          setInputValue("");
        }
      }}
      inputValue={inputValue}
      onInputChange={(
        _event: React.SyntheticEvent,
        nextInputValue: string,
        reason: string
      ) => {
        if (
          reason === "input" &&
          shouldBlockUnsafeSearchInput(nextInputValue)
        ) {
          return;
        }

        setInputValue(nextInputValue);
      }}
      onChange={(
        _e: React.SyntheticEvent,
        newValue: RpAutocompleteOption[]
      ) => {
        const newVals = Array.isArray(newValue)
          ? uniqueValues(
              newValue.filter((o) => !isPseudo(o.value)).map((o) => o.value)
            )
          : [];
        setPending(newVals);
      }}
      size={size}
      disabled={disabled}
      noOptionsText={noOptionsText}
      openOnFocus
      disableClearable
      disableCloseOnSelect
      disablePortal
      sx={autocompleteSx}
      id={name}
      onBlur={onBlur}
      slots={{
        paper: PaperWithApply,
        ...(useVirtualList ? { listbox: VirtualListbox } : {}),
      }}
      slotProps={{
        listbox: {
          ...(useVirtualList ? { onEndReached: onListEndReached } : {}),
          sx: {
            py: 1.25,
            "& .MuiAutocomplete-option": {
              minHeight: 40,
              px: 1.75,
              py: 0.75,
            },
          },
        },
      }}
      renderOption={(props: any, option: RpAutocompleteOption) => {
        const { key, ...liProps } = props;
        const hasFilteredOptions = filteredOptionsRef.current.length > 0;

        if (option.value === SELECT_ALL) {
          return (
            <Box key="__actions__">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 1.75,
                  py: 1,
                  gap: 1,
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!hasFilteredOptions) return;
                    setPending(uniqueValues(options.map((o) => o.value)));
                  }}
                  disabled={!hasFilteredOptions}
                  sx={{ fontSize: "0.7rem", py: 0.25, textTransform: "none" }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!hasFilteredOptions || pending.length === 0) return;
                    setPending([]);
                  }}
                  disabled={!hasFilteredOptions || pending.length === 0}
                  sx={{ fontSize: "0.7rem", py: 0.25, textTransform: "none" }}
                >
                  {clearLabel}
                </Button>
              </Box>
              <Divider />
            </Box>
          );
        }

        if (option.value === CLEAR_ALL) return null;

        if (option.value === NO_OPTIONS) {
          return (
            <Box key="__no_options__" sx={{ px: 1.75, py: 1.25 }}>
              <Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
                {noOptionsText}
              </Alert>
            </Box>
          );
        }

        return (
          <MenuItem key={option.value || key} {...liProps}>
            <Checkbox
              icon={UNCHECKED}
              checkedIcon={CHECKED}
              checked={pending.includes(option.value)}
              size="small"
              sx={{ py: 0, pl: 0, pr: 1.25 }}
            />
            <ListItemText
              primary={option.label}
              slotProps={{
                primary: { fontSize: "0.8125rem" },
              }}
            />
          </MenuItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          placeholder={committedChips.length === 0 ? placeholder : ""}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment:
              committedChips.length > 0 ? (
                <>
                  <Chip
                    label={committedChips[0].label}
                    size="small"
                    sx={{ maxWidth: "45%", mr: 0.5 }}
                  />
                  {committedChips.length > 1 && (
                    <Chip
                      label={`+${committedChips.length - 1}`}
                      size="small"
                      sx={{ mr: 0.5 }}
                    />
                  )}
                </>
              ) : null,
          }}
          slotProps={{
            inputLabel: staticLabel ? { shrink: true } : undefined,
            formHelperText: { sx: { minHeight: "1.25rem", mb: 0 } },
          }}
        />
      )}
    />
  );
}
